export interface QuickTask {
  title: string;
  prompt: string;
}

export interface ToolIntegration {
  id: string;
  name: string;
  baseUrl: string;
  authType: 'token' | 'basic';
  token?: string;
  username?: string;
  password?: string;
  logo?: string;
  enabled: boolean;
  skills: string[];
  quickTasks: QuickTask[];
  createdAt: number;
  updatedAt: number;
}

const STORE_KEY = 'toolIntegrations';

/** Normalize a raw quickTasks item — handles the old `string[]` format for backward compat */
function normalizeQuickTask(raw: any): QuickTask {
  if (typeof raw === 'string') {
    const title = raw.length > 24 ? raw.slice(0, 24) + '…' : raw;
    return { title, prompt: raw };
  }
  return raw as QuickTask;
}

function normalizeIntegration(raw: any): ToolIntegration {
  return {
    ...raw,
    quickTasks: Array.isArray(raw.quickTasks) ? raw.quickTasks.map(normalizeQuickTask) : [],
  };
}

class ToolIntegrationService {
  async getAll(): Promise<ToolIntegration[]> {
    const data = await window.electron.store.get(STORE_KEY);
    if (!data) return [];
    try {
      const parsed: any[] = typeof data === 'string' ? JSON.parse(data) : data;
      return Array.isArray(parsed) ? parsed.map(normalizeIntegration) : [];
    } catch {
      return [];
    }
  }

  async save(tools: ToolIntegration[]): Promise<void> {
    await window.electron.store.set(STORE_KEY, tools);
  }

  async add(tool: Omit<ToolIntegration, 'id' | 'createdAt' | 'updatedAt'>): Promise<ToolIntegration[]> {
    const tools = await this.getAll();
    const now = Date.now();
    const newTool: ToolIntegration = {
      ...tool,
      id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36),
      createdAt: now,
      updatedAt: now,
    };
    tools.push(newTool);
    await this.save(tools);
    return tools;
  }

  async update(id: string, partial: Partial<Omit<ToolIntegration, 'id' | 'createdAt'>>): Promise<ToolIntegration[]> {
    const tools = await this.getAll();
    const idx = tools.findIndex(t => t.id === id);
    if (idx === -1) return tools;
    tools[idx] = { ...tools[idx], ...partial, updatedAt: Date.now() };
    await this.save(tools);
    return tools;
  }

  async remove(id: string): Promise<ToolIntegration[]> {
    const tools = await this.getAll();
    const filtered = tools.filter(t => t.id !== id);
    await this.save(filtered);
    return filtered;
  }

  async getEnabled(): Promise<ToolIntegration[]> {
    const tools = await this.getAll();
    return tools.filter(t => t.enabled);
  }
}

export const toolIntegrationService = new ToolIntegrationService();
